/**
 * Maps raw microphone amplitude onto a 0..1 waveform sample suitable for drawing.
 *
 * Ported from the Flutter chat voice-note implementation
 * (`membership-flutter-app` PR #687, `amplitude_normalizer.dart`) so the web and
 * mobile waveforms share one shaping curve.
 *
 * The browser `AnalyserNode` exposes time-domain samples rather than dBFS, so
 * {@link rmsToDbfs} converts an RMS reading to dBFS first; {@link createAmplitudeNormalizer}
 * then maps dBFS to a drawable 0..1 sample:
 *   - clamp to a `[floorDb, ceilingDb]` window (below the floor = silence),
 *   - rescale that window to 0..1,
 *   - apply a gamma curve (`< 1`) so quiet passages lift into visible bars,
 *   - keep a small minimum so even silence renders as a faint bar.
 *
 * Pure and stateless: `normalize(dbfs)` has no side effects, which makes the
 * shaping curve trivially unit-testable and tunable in isolation.
 */

import type { AmplitudeNormalizer, AmplitudeNormalizerOptions } from '../types/voice.types'

/**
 * Defaults tuned for chat voice notes: quiet speech stays visible without
 * letting background silence dominate the waveform. Mirrors the Flutter values.
 */
export const AMPLITUDE_NORMALIZER_DEFAULTS: AmplitudeNormalizerOptions = {
  floorDb: -45,
  ceilingDb: -5,
  gamma: 0.6,
  minSample: 0.08,
  maxSample: 1.0,
  invalidInputSample: 0.1,
}

/** Finite dBFS floor returned for silent / non-positive RMS (avoids -Infinity). */
const SILENT_DBFS = -160

/**
 * Convert a linear RMS amplitude (`0..1`, as derived from an `AnalyserNode`
 * time-domain buffer) to dBFS. Zero/negative RMS maps to a finite floor rather
 * than `-Infinity` so downstream math stays safe.
 */
export function rmsToDbfs(rms: number): number {
  if (!Number.isFinite(rms) || rms <= 0) return SILENT_DBFS
  return 20 * Math.log10(rms)
}

/**
 * Build a pure dBFS → `[minSample, maxSample]` normalizer. Pass partial options
 * to override individual window/curve parameters; the rest fall back to
 * {@link AMPLITUDE_NORMALIZER_DEFAULTS}.
 */
export function createAmplitudeNormalizer(options: Partial<AmplitudeNormalizerOptions> = {}): AmplitudeNormalizer {
  const { floorDb, ceilingDb, gamma, minSample, maxSample, invalidInputSample } = {
    ...AMPLITUDE_NORMALIZER_DEFAULTS,
    ...options,
  }

  if (floorDb >= ceilingDb) {
    throw new Error('AmplitudeNormalizer: floorDb must be below ceilingDb')
  }

  const span = ceilingDb - floorDb

  return (dbfs: number): number => {
    if (!Number.isFinite(dbfs)) return invalidInputSample
    const clamped = Math.min(Math.max(dbfs, floorDb), ceilingDb)
    const norm = (clamped - floorDb) / span
    const shaped = Math.pow(norm, gamma)
    return Math.min(Math.max(shaped, minSample), maxSample)
  }
}
